import type { AnyValueMap } from '@opentelemetry/api-logs';
import { emitFatalLog } from './logger';
import { serializeError } from './serializeError';
import { CONFIG } from 'config/environment_variables';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { SESSION_STORAGE_KEYS } from 'constant/sessionStorage';
import { getPreviousNavigationPath } from './navigationTracker';

/** Vite custom event — see https://vite.dev/guide/build#load-error-handling */
export type VitePreloadErrorEvent = Event & {
  payload: unknown;
  preventDefault(): void;
};

export type VitePreloadFailureKind =
  | 'dynamic_import_fetch'
  | 'module_script_load'
  | 'mime_type_mismatch'
  | 'unknown';

export type VitePreloadErrorDetails = {
  summary: string;
  failureKind: VitePreloadFailureKind;
  chunkUrl?: string;
  chunkFile?: string;
  chunkHash?: string;
  error: AnyValueMap;
  context: AnyValueMap;
  http?: AnyValueMap;
};

/** @deprecated kept for cleanup in clearVitePreloadReloadGuard */
const PRELOAD_RELOAD_KEY = 'vite_preload_error_reloaded';
const PRELOAD_RELOAD_COUNT_KEY = 'vite_preload_reload_count';
const PRELOAD_RELOAD_AT_KEY = 'vite_preload_reload_at';
export const VITE_PRELOAD_ERROR_DETECTED = 'vite:preloadError:detected';

function getPreloadReloadCount(): number {
  return parseInt(sessionStorage.getItem(PRELOAD_RELOAD_COUNT_KEY) ?? '0', 10);
}

function bumpPreloadReloadCount(): number {
  const next = getPreloadReloadCount() + 1;
  sessionStorage.setItem(PRELOAD_RELOAD_COUNT_KEY, String(next));
  sessionStorage.setItem(PRELOAD_RELOAD_AT_KEY, String(Date.now()));
  return next;
}
const CHUNK_PROBE_TIMEOUT_MS = 3000;
// Pre-reload repair must actually finish downloading a multi-megabyte bundle
// on a slow connection — the exact condition that produces truncated cache
// entries. 3s would abort mid-download and leave the corrupted entry in place.
const CACHE_REPAIR_TIMEOUT_MS = 10_000;
// Give-up path: nothing waits on this repair (fire-and-forget), so give slow
// connections the full window to replace the bad entry before the user's next
// manual refresh.
const CACHE_REPAIR_BACKGROUND_TIMEOUT_MS = 30_000;

const CHUNK_URL_IN_MESSAGE =
  /(?:Failed to fetch dynamically imported module|error loading dynamically imported module|Loading (?:CSS )?chunk \d+ failed(?:\.\s*)?(?:\([^)]*\))?):\s*(https?:\/\/[^\s'"]+|\/[^\s'"]+)/i;

const ASSET_URL_PATTERN =
  /(?:https?:\/\/[^\s'"]+|\/[^\s'"]+)assets\/[^\s'")]+?\.(?:js|css|mjs)(?:\?[^\s'"]*)?/gi;

const HASHED_ASSET_FILENAME = /^(.+)-([a-f0-9]{8,})\.(js|css|mjs)$/i;

function classifyFailureKind(message: string): VitePreloadFailureKind {
  const normalized = message.toLowerCase();

  if (
    normalized.includes('mime type') ||
    normalized.includes('text/html') ||
    normalized.includes('expected a javascript')
  ) {
    return 'mime_type_mismatch';
  }

  if (
    normalized.includes('failed to fetch dynamically imported module') ||
    normalized.includes('error loading dynamically imported module') ||
    normalized.includes('loading chunk') ||
    normalized.includes('failed to fetch')
  ) {
    return 'dynamic_import_fetch';
  }

  if (normalized.includes('importing a module script failed')) {
    return 'module_script_load';
  }

  return 'unknown';
}

function parseChunkFilename(url: string): {
  chunkFile?: string;
  chunkHash?: string;
} {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const chunkFile = pathname.split('/').pop();
    if (!chunkFile) return {};

    const hashMatch = chunkFile.match(HASHED_ASSET_FILENAME);
    if (!hashMatch) {
      return { chunkFile };
    }

    return {
      chunkFile,
      chunkHash: hashMatch[2],
    };
  } catch {
    const chunkFile = url.split('/').pop()?.split('?')[0];
    if (!chunkFile) return {};
    const hashMatch = chunkFile.match(HASHED_ASSET_FILENAME);
    return hashMatch ? { chunkFile, chunkHash: hashMatch[2] } : { chunkFile };
  }
}

function collectErrorMessages(error: unknown): string[] {
  const messages: string[] = [];
  let current: unknown = error;

  while (current != null) {
    if (typeof current === 'string') {
      messages.push(current);
      break;
    }

    if (current instanceof Error) {
      messages.push(current.message);
      if (current.stack) {
        messages.push(current.stack);
      }
      current = (current as Error & { cause?: unknown }).cause;
      continue;
    }

    if (typeof current === 'object' && 'message' in current) {
      const message = (current as { message?: unknown }).message;
      if (typeof message === 'string') {
        messages.push(message);
      }
    }

    break;
  }

  return messages;
}

function extractChunkUrl(error: unknown): string | undefined {
  const haystack = collectErrorMessages(error).join('\n');

  const messageMatch = haystack.match(CHUNK_URL_IN_MESSAGE);
  if (messageMatch?.[1]) {
    return messageMatch[1].replace(/['"]+$/, '');
  }

  const assetMatches = haystack.match(ASSET_URL_PATTERN);
  if (assetMatches?.length) {
    return assetMatches[assetMatches.length - 1];
  }

  return undefined;
}

function serializeResponseHeaders(headers: Headers): AnyValueMap {
  const result: AnyValueMap = {};
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

function headerValue(
  http: AnyValueMap | undefined,
  name: string,
): string | undefined {
  const headers = http?.responseHeaders;
  if (headers == null || typeof headers !== 'object') return undefined;

  const value = (headers as Record<string, unknown>)[name.toLowerCase()];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Browsers do not attach HTTP response metadata to failed `import()` errors.
 * Probe the chunk URL directly so SigNoz receives status + headers (e.g.
 * content-type, cache-control) for stale-deploy diagnosis.
 */
async function probeChunkHttpResponse(chunkUrl: string): Promise<AnyValueMap> {
  const resolvedUrl = new URL(chunkUrl, window.location.origin).href;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    CHUNK_PROBE_TIMEOUT_MS,
  );

  try {
    let method: 'HEAD' | 'GET' = 'HEAD';
    let response = await fetch(resolvedUrl, {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });

    if (response.status === 405 || response.status === 501) {
      method = 'GET';
      response = await fetch(resolvedUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal,
      });
    }

    return {
      url: resolvedUrl,
      method,
      status: response.status,
      statusText: response.statusText,
      redirected: response.redirected,
      responseUrl: response.url,
      responseHeaders: serializeResponseHeaders(response.headers),
    };
  } catch (probeError) {
    const serialized = serializeError(probeError);
    return {
      url: resolvedUrl,
      probeError:
        typeof serialized.message === 'string'
          ? serialized.message
          : 'chunk HTTP probe failed',
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * Overwrites the browser's HTTP-cache entry for a failed chunk with a fresh
 * copy from the server.
 *
 * `cache: 'reload'` bypasses the local cache for this request AND replaces the
 * stored entry with the response — unlike the diagnostic probe's `no-store`,
 * which never touches the cache. This is the actual recovery for the
 * corrupted-cache failure mode (a truncated cached script evaluating with
 * "SyntaxError: Unexpected end of input"): the `_cb` reload busts only the
 * document URL, so without this repair the reloaded page re-executes the same
 * bad cached asset and fails identically (VDP-972).
 *
 * Returns the HTTP status on success or an error message string — recorded on
 * the crash report as `cacheRepairStatus` so SigNoz shows whether the repair
 * could run before the recovery reload.
 */
async function repairChunkCacheEntry(
  chunkUrl: string,
  timeoutMs: number = CACHE_REPAIR_TIMEOUT_MS,
): Promise<number | string> {
  const resolvedUrl = new URL(chunkUrl, window.location.origin).href;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(resolvedUrl, {
      cache: 'reload',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    return response.status;
  } catch (repairError) {
    const serialized = serializeError(repairError);
    return typeof serialized.message === 'string'
      ? serialized.message
      : 'cache repair fetch failed';
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * Collects every asset URL worth repairing for a preload failure.
 *
 * The URL extracted from the error is often missing (bare "SyntaxError:
 * Unexpected end of input" from a truncated script carries none) or
 * misattributed (stack-frame extraction returns whichever asset appears last).
 * The corrupted asset in the observed VDP-972 sessions was the module entry
 * bundle itself, which is always discoverable from the DOM — so include the
 * entry script(s) alongside whatever the error names.
 */
function collectRepairUrls(error: unknown): string[] {
  const urls = new Set<string>();

  const extracted = extractChunkUrl(error);
  if (extracted) {
    try {
      urls.add(new URL(extracted, window.location.origin).href);
    } catch {
      // Unparseable extraction — the DOM entry scripts below still apply.
    }
  }

  document
    .querySelectorAll<HTMLScriptElement>('script[type="module"][src]')
    .forEach((script) => {
      if (script.src.includes('/assets/')) {
        urls.add(script.src);
      }
    });

  return [...urls];
}

function repairUrlLabel(url: string): string {
  return url.split('/').pop()?.split('?')[0] ?? url;
}

/**
 * Repairs every candidate asset in parallel and returns a compact per-file
 * status summary (e.g. "index-CnnQ4r_i.js=200; ChunkA-x1.js=200") for the
 * crash report, or undefined when there was nothing to repair.
 */
async function repairAssetCacheEntries(
  repairUrls: string[],
  timeoutMs: number = CACHE_REPAIR_TIMEOUT_MS,
): Promise<string | undefined> {
  if (!repairUrls.length) return undefined;

  const statuses = await Promise.all(
    repairUrls.map(async (url) => {
      const status = await repairChunkCacheEntry(url, timeoutMs);
      return `${repairUrlLabel(url)}=${status}`;
    }),
  );

  return statuses.join('; ');
}

export function parseVitePreloadError(
  event: VitePreloadErrorEvent,
): VitePreloadErrorDetails {
  const importError = event.payload;
  const serializedError = serializeError(importError);
  const errorMessage =
    typeof serializedError.message === 'string' ? serializedError.message : '';
  const failureKind = classifyFailureKind(errorMessage);
  const chunkUrl = extractChunkUrl(importError);
  const { chunkFile, chunkHash } = chunkUrl ? parseChunkFilename(chunkUrl) : {};

  const summary = chunkFile
    ? `Vite chunk preload failed: ${chunkFile} (${failureKind})`
    : `Vite chunk preload failed (${failureKind})`;

  return {
    summary,
    failureKind,
    chunkUrl,
    chunkFile,
    chunkHash,
    error: serializedError,
    context: {
      pageHref: window.location.href,
      pagePath: window.location.pathname,
      fromPath: getPreviousNavigationPath() || undefined,
      referrer: document.referrer || undefined,
      appVersion: __APP_BUILD_VERSION__,
      userAgent: navigator.userAgent,
      userId: localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
      userEmail:
        sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail',
      reloadCount: getPreloadReloadCount(),
    },
  };
}

export async function reportVitePreloadError(
  event: VitePreloadErrorEvent,
  options?: {
    reloadAlreadyAttempted?: boolean;
    cacheRepairStatus?: string;
  },
): Promise<VitePreloadErrorDetails> {
  const details = parseVitePreloadError(event);
  const http = details.chunkUrl
    ? await probeChunkHttpResponse(details.chunkUrl)
    : undefined;
  const detailsWithHttp = { ...details, http };

  const userId =
    localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser';
  const userEmail =
    sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail';

  emitFatalLog({
    body: {
      message: details.summary,
      failureKind: details.failureKind,
      chunk: {
        url: details.chunkUrl,
        file: details.chunkFile,
        hash: details.chunkHash,
      },
      error: details.error,
      http,
      context: details.context,
      reloadAlreadyAttempted: options?.reloadAlreadyAttempted ?? false,
      cacheRepairStatus: options?.cacheRepairStatus,
    },
    attrs: {
      source: 'ViteCrash',
      'user.id': userId,
      'user.email': userEmail,
      failureKind: details.failureKind,
      chunkUrl: details.chunkUrl,
      chunkFile: details.chunkFile,
      chunkHash: details.chunkHash,
      errorName:
        typeof details.error.name === 'string' ? details.error.name : undefined,
      errorMessage:
        typeof details.error.message === 'string'
          ? details.error.message
          : undefined,
      httpStatus: typeof http?.status === 'number' ? http.status : undefined,
      httpContentType: headerValue(http, 'content-type'),
      httpCacheControl: headerValue(http, 'cache-control'),
      httpEtag: headerValue(http, 'etag'),
      appVersion: __APP_BUILD_VERSION__,
      reloadCount: getPreloadReloadCount(),
      reloadAlreadyAttempted: options?.reloadAlreadyAttempted ?? false,
      cacheRepairStatus: options?.cacheRepairStatus,
    },
  });

  return detailsWithHttp;
}

/**
 * Handles Vite dynamic-import preload failures (stale chunks after deploy).
 *
 * - Dev mode: logs only. Vite's HMR handles module recovery — reloading would
 *   cause disruptive loops during normal navigation in local development.
 * - Production: logs, then cache-bust reloads once. On repeat failure, notifies
 *   AppVersionUpdateListener instead of looping infinitely.
 *
 * @see https://vite.dev/guide/build#load-error-handling
 */
export function handleVitePreloadError(event: VitePreloadErrorEvent): void {
  void handleVitePreloadErrorAsync(event);
}

async function handleVitePreloadErrorAsync(
  event: VitePreloadErrorEvent,
): Promise<void> {
  // Always call preventDefault() first. If omitted, Vite's own vite:preloadError
  // default handler fires — in Vite 5+ / 8+ dev mode this triggers a full hard
  // page reload, which is exactly the "increased reloads during navigation" symptom.
  event.preventDefault();

  if (CONFIG.NODE_ENV === 'development') {
    // Dev mode: HMR handles module recovery. Skip the HTTP chunk probe (dev
    // server URLs are not hashed assets) and just log the error for visibility.
    const details = parseVitePreloadError(event);
    emitFatalLog({
      body: {
        message: details.summary,
        failureKind: details.failureKind,
        error: details.error,
        context: details.context,
      },
      attrs: {
        source: 'ViteCrash',
        'user.id':
          localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
        'user.email':
          sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail',
        failureKind: details.failureKind,
        chunkFile: details.chunkFile,
        appVersion: __APP_BUILD_VERSION__,
        isDev: true,
      },
    });
    return;
  }

  const reloadAlreadyAttempted = getPreloadReloadCount() >= 1;

  if (reloadAlreadyAttempted) {
    // No reload is about to happen on this pass, so there's no rush — await the
    // full report (including the HTTP probe) before notifying the fallback UI.
    await reportVitePreloadError(event, { reloadAlreadyAttempted });
    // Still repair the cached assets (fire-and-forget, generous timeout) so the
    // user's next manual refresh loads fresh copies instead of the corrupted
    // ones — without this, a wedged client stays wedged indefinitely.
    void repairAssetCacheEntries(
      collectRepairUrls(event.payload),
      CACHE_REPAIR_BACKGROUND_TIMEOUT_MS,
    );
    window.dispatchEvent(new Event(VITE_PRELOAD_ERROR_DETECTED));
    return;
  }

  // Repair the failing assets' HTTP-cache entries BEFORE the recovery reload.
  // Without this, the `_cb` reload only cache-busts the document: the reloaded
  // page requests the same asset URLs, hits the same corrupted cache entry, and
  // fails identically one second later (the exact loop in VDP-972's telemetry).
  // The await is bounded by CACHE_REPAIR_TIMEOUT_MS (repairs run in parallel);
  // the worst case is a brief ErrorBoundary flash before the reload fires — an
  // acceptable trade for a reload that can actually recover.
  const cacheRepairStatus = await repairAssetCacheEntries(
    collectRepairUrls(event.payload),
  );

  const url = new URL(window.location.href);
  url.searchParams.set('_cb', String(Date.now()));
  window.location.href = url.toString();

  // Fire-and-forget: reportVitePreloadError() must not gate the reload — its
  // HTTP probe alone can take up to 3s, long enough for React to render the
  // broken chunk and hit the ErrorBoundary before the reload ever fired.
  void reportVitePreloadError(event, {
    reloadAlreadyAttempted,
    cacheRepairStatus,
  });
  bumpPreloadReloadCount();
}

export function clearVitePreloadReloadGuard(): void {
  // Clear both the legacy key and the new counter keys.
  sessionStorage.removeItem(PRELOAD_RELOAD_KEY);
  sessionStorage.removeItem(PRELOAD_RELOAD_COUNT_KEY);
  sessionStorage.removeItem(PRELOAD_RELOAD_AT_KEY);
}
